import PageHero from "@/components/PageHero";
import { FadeIn, Chapter } from "@/components/Motion";

const SECTIONS = [
  {
    id: "controller",
    title: "Data Controller",
    body: "ACTIVE Stairlifts S.r.l., European Manufacturing Campus, is the data controller for all personal data collected through this website. You can reach our privacy office at privacy@activestairlifts.eu.",
  },
  {
    id: "data",
    title: "What We Collect",
    body: "When you submit an enquiry (distributor, OEM, ODM, download or contact form) we collect: company name, contact name, business email, phone number, country, brands currently sold, estimated installations and purchase volumes, and your message. We do not collect special-category data and this website is intended for professional visitors only.",
  },
  {
    id: "purpose",
    title: "Why We Process It",
    body: "We process enquiry data to respond to your request, evaluate distribution or manufacturing partnerships, and prepare commercial proposals. Legal basis: your explicit consent (GDPR Art. 6(1)(a)) given via the consent checkbox, and our legitimate interest in B2B business development (Art. 6(1)(f)).",
  },
  {
    id: "retention",
    title: "Storage & Retention",
    body: "Enquiry data is stored on European infrastructure and retained for a maximum of 24 months after our last contact, unless a commercial relationship requires longer retention. Data is never sold or shared with third parties for their own marketing.",
  },
  {
    id: "rights",
    title: "Your Rights",
    body: "Under GDPR you have the right to access, rectify, erase, restrict or port your personal data, and to withdraw consent at any time. To exercise any right, email privacy@activestairlifts.eu — we respond within 30 days. You may also lodge a complaint with your national data protection authority.",
  },
];

const COOKIES = [
  { name: "active_cookie_consent", purpose: "Remembers your cookie choice on this device.", duration: "Until cleared" },
  { name: "Essential session storage", purpose: "Required for page navigation and form security.", duration: "Session" },
];

export default function Privacy() {
  return (
    <>
      <PageHero
        overline="Legal"
        lines={["Privacy &", "Cookie Policy"]}
        sub="How ACTIVE Stairlifts handles professional enquiry data under the General Data Protection Regulation (GDPR). Last updated: August 2026."
      />

      <section className="bg-white pb-24" data-testid="privacy-sections">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter index="01" label="Data Protection" />
          <div className="space-y-0">
            {SECTIONS.map((s, i) => (
              <FadeIn key={s.id} delay={Math.min(i * 0.05, 0.25)}>
                <div className="py-8 border-b border-slate-200/80 first:border-t" data-testid={`privacy-${s.id}`}>
                  <div className="flex items-baseline gap-5">
                    <span className="font-mono text-[11px] text-signal tracking-[0.15em] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h2 className="font-display text-xl font-semibold tracking-tight text-slate-950">{s.title}</h2>
                      <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          <div id="cookies" className="pt-16">
            <Chapter index="02" label="Cookies" />
            <FadeIn>
              <h2 className="font-display text-xl font-semibold tracking-tight text-slate-950">Cookie Usage</h2>
              <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed max-w-2xl">
                This website uses no advertising, analytics or tracking cookies. Only strictly necessary storage is used:
              </p>
              <div className="mt-8 rounded-2xl border border-slate-200/80 overflow-hidden">
                {COOKIES.map((c, i) => (
                  <div key={c.name} className={`grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-6 px-6 py-5 ${i % 2 === 0 ? "bg-[#F8F9FA]/70" : "bg-white"}`} data-testid={`cookie-row-${i}`}>
                    <span className="font-mono text-[12px] text-slate-950 break-all">{c.name}</span>
                    <span className="text-sm text-slate-600">{c.purpose}</span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400 sm:text-right">{c.duration}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-slate-600 leading-relaxed max-w-2xl">
                To change your choice, clear this site's data in your browser settings — the consent banner will reappear on your next visit.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>
    </>
  );
}
